// autopilot.ts — client for the server's autopilot pipeline (research → draft → approve → post) and
// the cloud queue/prefs. Same auth pattern as brain.ts: the user's Oriora Supabase session as Bearer.
// All state lives server-side in the customer's own GitHub repo (queue.json / prefs.json) — we hold none.

import { getAccessToken } from './supabase';

const API = (import.meta as any).env?.VITE_API_URL || 'https://api.orioralabs.com';
const BASE = `${API}/api/apps/oriora-social`;

async function authed(path: string, init: RequestInit = {}): Promise<any> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in to Oriora.');
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body?.message || body?.error || `HTTP ${r.status}`);
  return body;
}

export interface QueueItem {
  id: string;
  draft: string;
  source?: string;
  status: 'pending' | 'approved' | 'rejected' | 'posted' | 'scheduled' | 'dispatching';
  platform?: string;
  post_url?: string | null;
  post_id?: string | null;
  scheduled_at?: string;
  /** A schedule proposed via chat — the time is a SUGGESTION until the human approves (HITL). */
  suggested_scheduled_at?: string;
  posted_at?: string;
  created_at?: string;
  dispatch_id?: string;
  media_path?: string;
  stats?: { likes: number; reposts: number; replies: number; fetched_at?: string };
  /** RULES-CHECK flags from the server's deterministic lint gate — shown next to the draft; never blocks. */
  lint?: string[];
  /** Set when this item was evergreen-recycled from an earlier posted item. */
  recycled_from?: string;
}

/** Platforms Oriora can post to directly (API). Everything else = the Copy button. */
export const WIRED_PLATFORMS = ['mastodon', 'bluesky', 'discord', 'telegram', 'x'];

/** Platforms that support media posting via GitHub Actions (X only for now — post-media.mjs). */
export const MEDIA_PLATFORMS = ['x'];

export const getQueue = (): Promise<QueueItem[]> => authed('/autopilot/queue').then((j) => (j.items ?? []) as QueueItem[]);
export const runDraft = (): Promise<QueueItem> => authed('/autopilot/draft', { method: 'POST', body: '{}' }).then((j) => j.item as QueueItem);
export const approve = (id: string): Promise<QueueItem> => authed(`/autopilot/queue/${id}/approve`, { method: 'POST' }).then((j) => j.item as QueueItem);
export const reject = (id: string): Promise<QueueItem> => authed(`/autopilot/queue/${id}/reject`, { method: 'POST' }).then((j) => j.item as QueueItem);
/** Evergreen recycling: duplicate a posted item back into review as pending (free, no AI call). */
export const recycle = (id: string): Promise<QueueItem> => authed(`/autopilot/queue/${id}/recycle`, { method: 'POST' }).then((j) => j.item as QueueItem);
export const editItem = (id: string, text: string): Promise<QueueItem> =>
  authed(`/autopilot/queue/${id}/edit`, { method: 'POST', body: JSON.stringify({ text }) }).then((j) => j.item as QueueItem);
export const postItem = (id: string, platform: string): Promise<QueueItem> =>
  authed(`/autopilot/queue/${id}/post`, { method: 'POST', body: JSON.stringify({ platform }) }).then((j) => j.item as QueueItem);
export const schedule = (id: string, platform: string, scheduledAt: string): Promise<QueueItem> =>
  authed(`/autopilot/queue/${id}/schedule`, { method: 'POST', body: JSON.stringify({ platform, scheduled_at: scheduledAt }) }).then((j) => j.item as QueueItem);
/** Pull live engagement onto posted items (cheap GET, no AI). Returns the refreshed queue. */
export const refreshStats = (): Promise<QueueItem[]> =>
  authed('/autopilot/refresh-stats', { method: 'POST', body: '{}' }).then((j) => (j.items ?? []) as QueueItem[]);

/** The customer's connected accounts (for the Accounts strip). Degrades to [] on any error. */
export async function getConnections(): Promise<{ service: string; connected: boolean; display_name?: string }[]> {
  const token = await getAccessToken();
  if (!token) return [];
  try {
    const r = await fetch(`${API}/connections`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return [];
    const j = await r.json();
    return Array.isArray(j) ? j : (j.connections ?? []);
  } catch { return []; }
}

export const getPrefs = (): Promise<Record<string, any> | null> => authed('/storage/prefs').then((j) => j.prefs ?? null);
export async function setAutopilot(on: boolean): Promise<void> {
  const prefs = (await getPrefs()) ?? {};
  await authed('/storage/prefs', { method: 'PUT', body: JSON.stringify({ prefs: { ...prefs, autopilot: on } }) });
}

/** Billable AI coaching: reads engagement + voice history → what's working + next-action. */
export const coachMe = (): Promise<{ coaching: string; amount_usd: number; amount_units: number }> =>
  authed('/autopilot/coach', { method: 'POST', body: '{}' });

export interface MediaFile { name: string; path: string; download_url: string | null; size: number; }
/** List files in the customer's media/ folder in their GitHub repo. */
export const listMedia = (): Promise<MediaFile[]> => authed('/media').then((j) => (j.files ?? []) as MediaFile[]);
/** Attach a media file to a queued item. Pass null to detach. */
export const attachMedia = (id: string, mediaPath: string): Promise<QueueItem> =>
  authed(`/autopilot/queue/${id}/media`, { method: 'POST', body: JSON.stringify({ media_path: mediaPath }) }).then((j) => j.item as QueueItem);
export const detachMedia = (id: string): Promise<QueueItem> =>
  authed(`/autopilot/queue/${id}/media`, { method: 'POST', body: JSON.stringify({ media_path: null }) }).then((j) => j.item as QueueItem);
/** Generate an image from a prompt via the customer's BYOK key (DALL-E 3 / gpt-4o). */
export const generateImage = (prompt: string): Promise<{ image_url: string; prompt: string; amount_usd: number; amount_units: number }> =>
  authed('/media/generate', { method: 'POST', body: JSON.stringify({ prompt }) });

// ── Video generation (the video lane) — all URL-only; bytes never transit Oriora ──
export interface VideoVendor { vendor: string; displayName: string; defaultModel?: string; keyCreationUrl?: string }
/** Video-capable BYOK vendors, from the same registry the website key wizard renders. */
export async function listVideoVendors(): Promise<VideoVendor[]> {
  const token = await getAccessToken();
  if (!token) return [];
  try {
    const r = await fetch(`${API}/byok/providers`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return [];
    const rows = await r.json();
    return (Array.isArray(rows) ? rows : [])
      .filter((x: any) => x.kind === 'video')
      .map((x: any) => ({ vendor: x.vendor, displayName: x.displayName, defaultModel: x.defaultModel, keyCreationUrl: x.rich?.keyCreationUrl }));
  } catch { return []; }
}
/** The provider slugs this account has keys for (from the website Keys tab). Degrades to []. */
export async function listConnectedProviderSlugs(): Promise<string[]> {
  const token = await getAccessToken();
  if (!token) return [];
  try {
    const r = await fetch(`${API}/auth/provider-keys`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return [];
    const rows = await r.json();
    // stored either bare ('ark') or prefixed ('direct:ark') — normalize to bare
    return (Array.isArray(rows) ? rows : []).map((x: any) => String(x.provider ?? '').replace(/^direct:/, ''));
  } catch { return []; }
}
export const generateVideo = (p: { vendor: string; prompt: string; image_url?: string; resolution?: string }): Promise<{ task_id: string; vendor: string; model: string }> =>
  authed('/media/generate-video', { method: 'POST', body: JSON.stringify(p) });
export const videoStatus = (vendor: string, taskId: string, model: string): Promise<{ status: string; video_url?: string; error?: string }> =>
  authed(`/media/video-status?vendor=${encodeURIComponent(vendor)}&task_id=${encodeURIComponent(taskId)}&model=${encodeURIComponent(model)}`);
/** Save a generated-media URL into the customer's library — their GitHub Actions moves the bytes. */
export const saveMedia = (url: string, filename: string): Promise<{ dispatch_id: string }> =>
  authed('/media/save', { method: 'POST', body: JSON.stringify({ url, filename }) });
export const saveResult = (dispatchId: string): Promise<{ status: string; path?: string; motion?: number; duration_sec?: number; dimensions?: string }> =>
  authed(`/media/save-result/${encodeURIComponent(dispatchId)}`);

/** Phase 3/4 — dispatch a GH Actions workflow to post a draft + media file. Returns immediately with a dispatch_id. */
export const postWithMedia = (draftId: string, mediaPath: string, platform: string): Promise<{ dispatch_id: string; status: string; message: string }> =>
  authed('/autopilot/post-with-media', { method: 'POST', body: JSON.stringify({ draftId, mediaPath, platform }) });

/** Poll the result of a media post dispatched via postWithMedia(). */
export const pollPostResult = (dispatchId: string): Promise<{ status: string; post_id?: string; post_url?: string }> =>
  authed(`/autopilot/post-result/${encodeURIComponent(dispatchId)}`);
